import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductGridCard from "@insite/content-library/Widgets/ProductList/ProductListProductGridCard";
import ProductListTable from "@insite/content-library/Widgets/ProductList/ProductListTable";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setPLPRendering from "@ucs/Handlers/SetPLPRendering";
import { getPLPRendering } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import ProductGridItem from "@ucs/Widgets/ProductGridItem";
import React, { FC, ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {}

interface OwnProps extends WidgetProps {
    fields: {};
}

const mapStateToProps = (state: CustomState, ownProps: OwnProps) => ({
    isLoading: state.pages.productList.isLoading,
    productsDataView: getProductListDataView(state),
    // TODO ISC-13448 we no need ProductListSettings DefaultView, besause we use widget property productListDefaultView instead
    // view: state.pages.productList.view || ownProps.fields.productListDefaultView,
    view: state.pages.productList.view,
    gridHeightState: state.customReducer.gridHeight,
});

const mapDispatchToProps = {
    setView,
    setPLPRendering,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 15px;
            /* border-top: 1px solid ${getColor("common.border")}; */
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noProductsText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
            `,
        },
        gridItem: {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
};

const styles = listStyles;

const GridWrapper = styled("div")<{ view: string | undefined }>`
    display: flex;
    overflow: hidden;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: flex-start;
    width: 100%;
    ${({ view, theme }) => {
        if (view === "List") {
                const _css = css`
                    display: flex;
                    flex-wrap: nowrap;
                    flex-direction: column;
                `;
                return breakpointMediaQueries(theme, [
                    null, null, _css, _css, _css
                ]);
        }
    }};
`;


const ProductGrid: FC<Props> = ({ isLoading, productsDataView, view, fields, setView, gridHeightState, setPLPRendering }) => {

    if (isLoading && !productsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper} style={{minHeight: gridHeightState}}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    // setPLPRendering({gridHeight: 0});

    React.useEffect(() => {
        const sessionGridHeight = sessionStorage.getItem("plpGridHeight");
        if (sessionGridHeight) {
            setPLPRendering({gridHeight: Number(sessionGridHeight)});
            sessionStorage.removeItem("plpGridHeight");
        };

        const checkIfMobile = () => {
            if (window.innerWidth <= 1200 && view !== "Grid") {
                setView({view: "Grid"});
            }
        };

        if (!view || (window.innerWidth <= 1200 && view !== "Grid")) {
            setView({ view: "Grid" });
        }

        window.addEventListener("resize", checkIfMobile);

        return () => {
            window.removeEventListener("resize", checkIfMobile);
        };
    }, []);

    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;

    if (products.length === 0) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    <Typography {...styles.noProductsText}>{translate("No products found")}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const renderProducts = (children: ReactNode) => {
        return products.map(product => (
            <ProductListProductContext product={product} key={product.id}>
                {children}
            </ProductListProductContext>
        ));
    };

    return (
        <StyledWrapper {...styles.wrapper} id="productGridWrapper" style={{ minHeight: gridHeightState }}>
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading}>
                <GridWrapper view={view}>
                    {renderProducts(<ProductGridItem view={view} hideCompare={false} />)}
                </GridWrapper>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductGrid),
    definition: {
        group: "Product List",
        displayName: "Product Grid",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
